import { hasThreeCompleteDirections, hasThreeComparedSummaries } from './development-model';
import { PublicLabelOcr } from './public-label-ocr';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  emptyDevelopment,
  developmentContentSchema,
  type DevelopmentContent,
  type DevelopmentDirection,
} from '@sqi/shared-schemas';
import { evaluateDevelopmentFormula, indicativeDevelopmentCost } from '@sqi/formula-core';
import { request, uploadFile, fileBlob, type OemWorkspace } from '../../lib/oem-client';
import { runtimeMode, type OemContext } from '../../lib/auth';
import {
  developmentBlockers,
  developmentCostLabels,
  developmentCharge,
  updateDevelopmentCharge,
  chargeAmountFromInput,
  draftDirections,
  customerDirectionIndex,
  type DirectionSelection,
} from './development-model';
import { DevelopmentLibrary, type QualifiedBase } from './development-library';
import { renderDevelopmentPdf, downloadDevelopment } from './development-export';
interface Revision {
  id: string;
  anchorId: string;
  rootId: string;
  version: number;
  hash: string;
  content: DevelopmentContent;
  createdBy: string;
  directionSelection?: DirectionSelection;
}
interface DevelopmentList {
  revisions: Revision[];
  decisions: { revisionId: string; action: string; comment: string; selection?: number }[];
}
const fields: Record<string, string> = {
  label: 'ชื่อโครงการอ้างอิง',
  referenceUrl: 'ลิงก์สินค้าอ้างอิงสาธารณะ (ไม่บังคับ)',
  market: 'ตลาดปลายทาง',
  texture: 'เนื้อสัมผัส',
  fragrance: 'กลิ่นที่ต้องการ',
  benefits: 'ประโยชน์ที่คาดหวัง · ยังไม่ใช่ claim ที่พิสูจน์',
  avoidedIngredients: 'ส่วนประกอบที่ต้องการหลีกเลี่ยง',
  fillSize: 'ขนาดบรรจุ',
  quantity: 'จำนวน',
  factoryTargetPrice: 'เป้าต้นทุนผลิตต่อหน่วย · ไม่บังคับ',
  retailTargetPrice: 'เป้าราคาขายต่อหน่วย · ไม่บังคับ',
  densityGml: 'ความหนาแน่น g/mL ที่วัดจริง',
  yieldFraction: 'usable yield 0–1 ที่ยืนยัน',
};
const technicalLabels: Record<string, string> = {
  process: 'เฟสและกระบวนการ',
  equipment: 'เครื่องมือ',
  targets: 'ค่าเป้าหมายและเกณฑ์',
  preservation: 'ระบบกันเสียและหลักฐาน',
  tests: 'แผนทดสอบ',
  compatibility: 'ความเข้ากันได้',
  substitutions: 'การทดแทน',
  revalidation: 'ขอบเขตทบทวนใหม่',
  assumptions: 'ข้อสมมติและหลักฐาน',
  risks: 'ความเสี่ยง',
  balancing: 'การปัดเศษและ balancing',
};
async function derivative(file: Blob, rotation: number) {
  const bitmap = await createImageBitmap(file);
  try {
    if (bitmap.width * bitmap.height > 24000000 || bitmap.width > 8000 || bitmap.height > 8000)
      throw Error('ภาพใหญ่เกินขีดจำกัด');
    const scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale),
      h = Math.round(bitmap.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = rotation % 180 ? h : w;
    canvas.height = rotation % 180 ? w : h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw Error('ไม่สามารถประมวลผลภาพ');
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.drawImage(bitmap, -w / 2, -h / 2, w, h);
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(Error('image_decode_failed'))), 'image/png'),
    );
    return new File([blob], 'working.png', { type: 'image/png' });
  } finally {
    bitmap.close();
  }
}
function PhotoPreview({ id }: { id: string }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    let alive = true,
      href = '';
    void fileBlob(id)
      .then((b) => {
        href = URL.createObjectURL(b);
        if (alive) setUrl(href);
      })
      .catch(() => {
        /* scoped file may be unavailable */
      });
    return () => {
      alive = false;
      if (href) URL.revokeObjectURL(href);
    };
  }, [id]);
  return url ? (
    <img
      src={url}
      alt="ภาพอ้างอิงสำหรับตรวจฉลาก"
      style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain' }}
    />
  ) : (
    <p>ภาพยังไม่พร้อมแสดง</p>
  );
}
export function ReferenceDevelopment({
  context,
  workspace,
  userId,
  online,
  selectedRevisionId = null,
  onSelectedRevisionChange,
}: {
  context: OemContext;
  workspace: OemWorkspace;
  userId: string;
  online: boolean;
  selectedRevisionId?: string | null;
  onSelectedRevisionChange?: (id: string | null) => void;
}) {
  const [data, setData] = useState<DevelopmentList>({ revisions: [], decisions: [] });
  const [current, setCurrent] = useState<Revision | null>(null);
  const [content, setContent] = useState<DevelopmentContent>(emptyDevelopment());
  const [savedContent, setSavedContent] = useState<DevelopmentContent | null>(null);
  const [commandCandidate, setCommandCandidate] = useState<number | null>(null);
  const [commandDirection, setCommandDirection] = useState<number | null>(null);
  const [commandFeedback, setCommandFeedback] = useState('');
  const [commandChange, setCommandChange] = useState('');
  const [library, setLibrary] = useState<QualifiedBase[]>([]);
  const [libraryId, setLibraryId] = useState('');
  const dirty = !!current && JSON.stringify(content) !== JSON.stringify(savedContent);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [side, setSide] = useState<'front' | 'back' | 'label'>('front');
  const [notice, setNotice] = useState('');
  const [aiRun, setAiRun] = useState<{ id: string; state: string } | null>(null);
  const [sampleQuantity, setSampleQuantity] = useState('100');
  const [samplePolicy, setSamplePolicy] = useState('');
  const live = useRef(true),
    epoch = useRef(0),
    rememberedId = useRef(selectedRevisionId);
  const selectionScope = JSON.stringify([
    userId,
    context.organizationId,
    context.projectId,
    context.audience,
    context.role,
  ]);
  useLayoutEffect(() => {
    rememberedId.current = selectedRevisionId;
  }, [selectedRevisionId]);
  const technical = context.audience === 'staff' && context.readBundles.includes('technical');
  const author =
    context.actions.includes('create') &&
    (technical || context.audience === 'customer') &&
    (!current || technical || current.createdBy === userId);
  const selectedDecisions = data.decisions
    .filter((d) => d.revisionId === current?.id)
    .map((d) => d.action);
  const selectionRevision = data.revisions.find((r) => r.id === current?.id) ?? current;
  const selectedDirection = selectionRevision
    ? customerDirectionIndex(
        selectionRevision.directionSelection,
        selectionRevision,
        content.directions.length,
      )
    : null;
  async function refresh() {
    const generation = epoch.current;
    const fresh = (await request('/development/' + context.projectId)) as DevelopmentList;
    if (!live.current || generation !== epoch.current) return null;
    setData(fresh);
    return fresh;
  }
  const select = useCallback(
    (r: Revision) => {
      if (!live.current) return;
      setCurrent(r);
      const normalized = {
        ...emptyDevelopment(),
        ...r.content,
        photos: r.content.photos ?? [],
        candidates: r.content.candidates.map((c) => ({ ...c, inciLists: c.inciLists ?? [] })),
        directions: r.content.directions.map((d) => ({ ...d, lines: d.lines ?? [] })),
      };
      setContent(normalized);
      setSavedContent(structuredClone(normalized));
      setCommandCandidate(normalized.selectedCandidate);
      setCommandDirection(
        customerDirectionIndex(r.directionSelection, r, normalized.directions.length),
      );
      setCommandFeedback('');
      setCommandChange('');
      setError('');
      onSelectedRevisionChange?.(r.id);
    },
    [onSelectedRevisionChange],
  );
  useEffect(() => {
    live.current = true;
    const generation = ++epoch.current;
    if (runtimeMode === 'connected')
      void request('/development/' + context.projectId)
        .then((value) => {
          if (!live.current || generation !== epoch.current) return;
          const fresh = value as DevelopmentList;
          setData(fresh);
          setCurrent(null);
          setContent(emptyDevelopment());
          setSavedContent(null);
          const id = rememberedId.current;
          if (id) {
            const row = fresh.revisions.find((r) => r.id === id);
            if (row) select(row);
            else onSelectedRevisionChange?.(null);
          }
        })
        .catch(() => {
          if (live.current && generation === epoch.current) {
            setError('โหลดข้อมูลไม่ได้ · กรุณาตรวจการเชื่อมต่อและสิทธิ์');
            onSelectedRevisionChange?.(null);
          }
        });
    return () => {
      live.current = false;
      epoch.current = generation + 1;
    };
  }, [selectionScope, context.projectId, select, onSelectedRevisionChange]);
  async function run(fn: () => Promise<void>) {
    if (busy || !online) return;
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'ไม่สามารถบันทึกได้';
      setError(
        /customer_direction_(?:change|conflict)_requires_successor|bound_direction_mismatch_requires_successor/.test(
          message,
        )
          ? 'การเลือกของฉบับนี้เปลี่ยนไม่ได้หรือมีข้อขัดแย้ง กรุณาสร้างและแบ่งปันฉบับใหม่ให้ลูกค้าเลือกอีกครั้ง'
          : message,
      );
    } finally {
      setBusy(false);
    }
  }
  async function save(next = content, base = current) {
    const generation = epoch.current;
    const basisChanged =
      base &&
      (JSON.stringify(next.intake) !== JSON.stringify(base.content.intake) ||
        JSON.stringify(next.photos) !== JSON.stringify(base.content.photos) ||
        JSON.stringify(next.candidates) !== JSON.stringify(base.content.candidates));
    const parsed = developmentContentSchema.parse(
      basisChanged
        ? {
            ...next,
            identityConfirmed: false,
            briefConfirmed: false,
            directions: [],
            selectedDirection: null,
            sample: { ...next.sample, requestId: null, sampleId: null, sampleHash: null },
          }
        : next,
    );
    let anchorId = base?.anchorId;
    if (!anchorId) {
      const r = (await request('/commands/create', {
        projectId: context.projectId,
        skuId: null,
        target: null,
        idempotencyKey: crypto.randomUUID(),
        payload: {
          kind: 'brief',
          content: {
            label: parsed.intake.label,
            summary: 'Reference development draft · ยังไม่ครบ',
            market: parsed.intake.market,
          },
        },
      })) as { recordId: string };
      anchorId = r.recordId;
    }
    const result = (await request('/development', {
      projectId: context.projectId,
      anchorId,
      baseId: base?.id ?? null,
      baseHash: base?.hash ?? null,
      idempotencyKey: crypto.randomUUID(),
      content: {
        ...parsed,
        revisionReason: base ? parsed.revisionReason || 'บันทึกการเปลี่ยนแปลงร่างอ้างอิง' : '',
      },
    })) as { id: string; version: number; hash: string };
    const row: Revision = {
      ...result,
      anchorId,
      rootId: base?.rootId ?? result.id,
      content: parsed,
      createdBy: userId,
    };
    if (!live.current || generation !== epoch.current) throw Error('scope_changed');
    setCurrent(row);
    onSelectedRevisionChange?.(row.id);
    setContent(parsed);
    setSavedContent(structuredClone(parsed));
    await refresh();
    setNotice(`บันทึกฉบับ v${row.version} แล้ว · การตัดสินใจเดิมยังอยู่ แต่ต้องทบทวนฉบับใหม่นี้`);
    return row;
  }
  async function addPhotos(files: File[]) {
    if (content.photos.length + files.length > 12) throw Error('รับได้ไม่เกิน 12 ภาพ');
    const base = await save();
    let next = structuredClone(content);
    for (const file of files) {
      if (!['image/png', 'image/jpeg'].includes(file.type) || file.size > 20971520)
        throw Error('รับเฉพาะ PNG/JPEG ไม่เกิน 20 MiB');
      const working = await derivative(file, 0);
      const original = await uploadFile(
        context.projectId,
        base.anchorId,
        'shared',
        file,
        'Reference original — private',
      );
      const derived = await uploadFile(
        context.projectId,
        base.anchorId,
        'shared',
        working,
        'Metadata-free working derivative',
      );
      next = {
        ...next,
        photos: [
          ...next.photos,
          {
            fileId: original.id,
            derivativeId: derived.id,
            side,
            rotation: 0,
            rawOcr: '',
            correctedOcr: '',
            quality: 'unreviewed',
          },
        ],
      };
    }
    await save(next, base);
  }
  function updateDirection(i: number, next: DevelopmentDirection) {
    if (
      next.origin === 'approved_base' &&
      JSON.stringify(
        next.lines.map((l) => [l.inci, l.grade, l.percent, l.activeFraction, l.phase]),
      ) !==
        JSON.stringify(
          content.directions[i]?.lines.map((l) => [
            l.inci,
            l.grade,
            l.percent,
            l.activeFraction,
            l.phase,
          ]),
        )
    ) {
      next = {
        ...next,
        origin: 'modified_base',
        revalidation:
          next.revalidation + ' · เปลี่ยนองค์ประกอบจากฐาน: ต้องทบทวน R&D/QA/ตัวอย่างใหม่',
      };
    }
    setContent({ ...content, directions: content.directions.map((d, n) => (n === i ? next : d)) });
  }
  async function exportSnapshot(audience: 'customer' | 'internal', pdf: boolean) {
    if (!current) throw Error('ต้องบันทึกฉบับก่อน');
    const s = (await request(`/development/${current.id}/export/${audience}`)) as Parameters<
      typeof renderDevelopmentPdf
    >[0];
    downloadDevelopment(
      pdf
        ? await renderDevelopmentPdf(s)
        : new Blob([JSON.stringify(s, null, 2)], { type: 'application/json' }),
      `sqi-development-${audience}-v${s.version}.${pdf ? 'pdf' : 'json'}`,
    );
  }
  if (runtimeMode !== 'connected')
    return (
      <section className="card">
        <h1>พัฒนาจากสินค้าอ้างอิง</h1>
        <p>หน้านี้ต้องใช้ connected preview เพื่อบันทึกหลักฐานจริง · fixture UI ไม่ใช่งานวิจัยสด</p>
      </section>
    );
  return (
    <>
      <div className="page-title">
        <div>
          <h1>พัฒนาจากสินค้าอ้างอิง</h1>
          <p>ร่างทดลอง · เก็บหลักฐานและประวัติ · ไม่ใช่การอนุมัติสูตรหรือผลิตจริง</p>
        </div>
        <button
          className="action secondary"
          disabled={busy}
          onClick={() => {
            setCurrent(null);
            setContent(emptyDevelopment());
            onSelectedRevisionChange?.(null);
          }}
        >
          เริ่มร่างใหม่
        </button>
      </div>
      <section className="card" aria-label="ขั้นตอนพัฒนาจากภาพ">
        <h2>ภาพอ้างอิง → ยืนยันรายละเอียด → เปรียบเทียบ 3 แนวทาง → R&D ทบทวน</h2>
        <p>บันทึกฉบับใหม่ทุกครั้งที่แก้หลักฐานหรือแนวทาง · ผู้ทบทวนต้องเป็นคนละคนกับผู้เขียน</p>
        <p role="status">
          ภาพ {content.photos.length} ภาพ ·{' '}
          {content.identityConfirmed && content.briefConfirmed
            ? 'ยืนยันรายละเอียดแล้ว'
            : 'รอยืนยันรายละเอียด'}{' '}
          · แนวทาง {content.directions.length}/3
        </p>
        {!(technical ? hasThreeCompleteDirections(content) : hasThreeComparedSummaries(content)) ? (
          <p className="note-warning">
            ต้องมี 3 แนวทาง พร้อมเหตุผล ความเสี่ยง และสิ่งที่ต้องทบทวน ก่อนแบ่งปัน เลือก หรือส่งให้
            R&D
          </p>
        ) : null}
      </section>
      <div className="review-layout reference-development-layout">
        <aside className="card">
          <h2>ฉบับที่บันทึก</h2>
          {data.revisions.map((r) => (
            <button
              key={r.id}
              className={`queue-item ${current?.id === r.id ? 'active' : ''}`}
              onClick={() => select(r)}
            >
              <strong>{r.content.intake.label}</strong>
              <span>
                v{r.version} · {r.hash.slice(0, 12)}
              </span>
            </button>
          ))}
        </aside>
        <section className="card">
          <fieldset disabled={busy || !online || !author}>
            <h2>1. ความต้องการ</h2>
            <div className="form-grid">
              {Object.entries(fields)
                .filter(([key]) => technical || !['densityGml', 'yieldFraction'].includes(key))
                .map(([key, label]) => (
                  <label key={key}>
                    {label}
                    <input
                      value={content.intake[key as keyof typeof content.intake] ?? ''}
                      onChange={(e) =>
                        setContent({
                          ...content,
                          intake: {
                            ...content.intake,
                            [key]: [
                              'fillSize',
                              'quantity',
                              'factoryTargetPrice',
                              'retailTargetPrice',
                              'densityGml',
                              'yieldFraction',
                            ].includes(key)
                              ? e.target.value || null
                              : e.target.value,
                          },
                        })
                      }
                    />
                  </label>
                ))}
              <label>
                ประเภท
                <select
                  value={content.intake.category}
                  onChange={(e) =>
                    setContent({
                      ...content,
                      intake: {
                        ...content.intake,
                        category: e.target.value as DevelopmentContent['intake']['category'],
                      },
                      identityConfirmed: false,
                      briefConfirmed: false,
                      directions: [],
                    })
                  }
                >
                  <option value="unknown">ยังไม่ทราบ · ต้องจำแนก</option>
                  <option value="moisturizer">Moisturizer</option>
                  <option value="body_wash">Body wash</option>
                  <option value="cosmetic_serum">Cosmetic serum</option>
                  <option value="specialist">ประเภทอื่น / ความเสี่ยงสูง · ผู้เชี่ยวชาญ</option>
                </select>
              </label>
              <label>
                หน่วย
                <select
                  value={content.intake.fillUnit}
                  onChange={(e) =>
                    setContent({
                      ...content,
                      intake: { ...content.intake, fillUnit: e.target.value as 'g' | 'mL' },
                    })
                  }
                >
                  <option>g</option>
                  <option>mL</option>
                </select>
              </label>
            </div>
            <h2>2. ภาพและ OCR</h2>
            <p>
              บันทึกร่างที่ยังไม่ครบอัตโนมัติก่อนอัปโหลด · ต้นฉบับเก็บแยกจากภาพทำงานที่ตัด metadata
              · PNG/JPEG ไม่เกิน 20 MiB และ 12.5 ล้านพิกเซลต่อภาพ
            </p>
            <label>
              ด้านของภาพ
              <select value={side} onChange={(e) => setSide(e.target.value as typeof side)}>
                <option value="front">ด้านหน้า</option>
                <option value="back">ด้านหลัง</option>
                <option value="label">ฉลากส่วนประกอบ</option>
              </select>
            </label>
            <label>
              เพิ่มหลายภาพ
              <input
                type="file"
                accept="image/png,image/jpeg"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []);
                  void run(() => addPhotos(files));
                  e.target.value = '';
                }}
              />
            </label>
            {content.photos.map((p, i) => (
              <section className="card" key={p.fileId}>
                <PhotoPreview id={p.derivativeId} />
                <p>
                  {p.side} · Original {p.fileId}
                </p>
                {current && runtimeMode === 'connected' && author && (
                  <PublicLabelOcr
                    key={`${userId}:${context.projectId}:${current.id}:${p.derivativeId}:${p.rotation}`}
                    revisionId={current.id}
                    revisionHash={current.hash}
                    derivativeId={p.derivativeId}
                    scopeKey={`${userId}:${context.projectId}`}
                    enabled={online && !dirty && !busy}
                    onApplied={async (id) => {
                      const updated = await refresh();
                      if (!updated) return;
                      const row = updated.revisions.find((r) => r.id === id);
                      if (row) {
                        select(row);
                        setNotice(
                          `บันทึก OCR ฉบับ v${row.version} แล้ว · ผลดิบและฉบับเดิมยังอยู่ ต้องทบทวนการยืนยันใหม่`,
                        );
                      }
                    }}
                  />
                )}

                <label>
                  คุณภาพ
                  <select
                    value={p.quality}
                    onChange={(e) =>
                      setContent({
                        ...content,
                        photos: content.photos.map((x, n) =>
                          n === i ? { ...x, quality: e.target.value as typeof p.quality } : x,
                        ),
                      })
                    }
                  >
                    {['unreviewed', 'readable', 'blurred', 'incomplete'].map((q) => (
                      <option key={q}>{q}</option>
                    ))}
                  </select>
                </label>
                <label>
                  OCR ดิบ / ถอดข้อความต้นฉบับ
                  <textarea
                    value={p.rawOcr}
                    onChange={(e) =>
                      setContent({
                        ...content,
                        photos: content.photos.map((x, n) =>
                          n === i ? { ...x, rawOcr: e.target.value } : x,
                        ),
                      })
                    }
                  />
                </label>
                <label>
                  ข้อความแก้ไขหลังตรวจภาพ
                  <textarea
                    value={p.correctedOcr}
                    onChange={(e) =>
                      setContent({
                        ...content,
                        photos: content.photos.map((x, n) =>
                          n === i ? { ...x, correctedOcr: e.target.value } : x,
                        ),
                      })
                    }
                  />
                </label>
                <button
                  className="action secondary"
                  onClick={() =>
                    void run(async () => {
                      const b = await save();
                      const rotation = ((p.rotation + 90) % 360) as typeof p.rotation;
                      const d = await uploadFile(
                        context.projectId,
                        b.anchorId,
                        'shared',
                        await derivative(await fileBlob(p.fileId), rotation),
                        'Rotated metadata-free derivative',
                      );
                      await save(
                        {
                          ...content,
                          photos: content.photos.map((x, n) =>
                            n === i ? { ...x, rotation, derivativeId: d.id } : x,
                          ),
                        },
                        b,
                      );
                    })
                  }
                >
                  หมุน 90°
                </button>
                <button
                  className="action secondary"
                  onClick={() =>
                    setContent({
                      ...content,
                      photos: content.photos.filter((_, n) => n !== i),
                      identityConfirmed: false,
                    })
                  }
                >
                  นำออกจากร่าง · ต้นฉบับยังอยู่
                </button>
              </section>
            ))}
            <h2>3. ตัวตน รุ่น และหลักฐาน</h2>
            <button
              className="action secondary"
              onClick={() =>
                setContent({
                  ...content,
                  candidates: [
                    ...content.candidates,
                    {
                      brand: '',
                      product: '',
                      referenceMarket: '',
                      referenceSize: '',
                      identity: '',
                      variant: '',
                      inciLists: [],
                      evidence: [],
                    },
                  ],
                  identityConfirmed: false,
                })
              }
            >
              เพิ่มตัวเลือกสินค้า / รุ่น
            </button>
            {content.candidates.map((candidate, i) => (
              <section className="card" key={i}>
                {(['brand', 'product', 'referenceMarket', 'referenceSize'] as const).map((key) => (
                  <label key={key}>
                    {
                      {
                        brand: 'แบรนด์อ้างอิง',
                        product: 'ชื่อสินค้าอ้างอิง',
                        referenceMarket: 'ตลาดของสินค้ารุ่นอ้างอิง',
                        referenceSize: 'ขนาดของสินค้าอ้างอิง',
                      }[key]
                    }
                    <input
                      value={candidate[key]}
                      onChange={(e) =>
                        setContent({
                          ...content,
                          candidates: content.candidates.map((x, n) =>
                            n === i ? { ...x, [key]: e.target.value } : x,
                          ),
                        })
                      }
                    />
                  </label>
                ))}
                <label>
                  ตัวตนสินค้า
                  <input
                    value={candidate.identity}
                    onChange={(e) =>
                      setContent({
                        ...content,
                        candidates: content.candidates.map((x, n) =>
                          n === i ? { ...x, identity: e.target.value } : x,
                        ),
                      })
                    }
                  />
                </label>
                <label>
                  รุ่น / ตลาด / ความต่าง
                  <input
                    value={candidate.variant}
                    onChange={(e) =>
                      setContent({
                        ...content,
                        candidates: content.candidates.map((x, n) =>
                          n === i ? { ...x, variant: e.target.value } : x,
                        ),
                      })
                    }
                  />
                </label>
                <label>
                  INCI แต่ละรายการ · เก็บข้อมูลขัดแย้งแยกบรรทัด
                  <textarea
                    value={candidate.inciLists.join('\n')}
                    onChange={(e) =>
                      setContent({
                        ...content,
                        candidates: content.candidates.map((x, n) =>
                          n === i ? { ...x, inciLists: e.target.value.split('\n') } : x,
                        ),
                      })
                    }
                  />
                </label>
                <button
                  className="action secondary"
                  onClick={() =>
                    setContent({
                      ...content,
                      candidates: content.candidates.map((x, n) =>
                        n === i
                          ? {
                              ...x,
                              evidence: [
                                ...x.evidence,
                                {
                                  audience: 'customer',
                                  field: 'identity',
                                  value: '',
                                  source: 'ตรวจจากภาพ/แหล่งที่ระบุ',
                                  url: '',
                                  observedAt: new Date().toISOString().slice(0, 10),
                                  uncertainty: 'unverified',
                                },
                              ],
                            }
                          : x,
                      ),
                    })
                  }
                >
                  เพิ่มหลักฐานรายฟิลด์
                </button>
                {candidate.evidence.map((e, j) => (
                  <div className="form-grid" key={j}>
                    {['field', 'value', 'source', 'url', 'observedAt'].map((k) => (
                      <label key={k}>
                        {k}
                        <input
                          value={e[k as keyof typeof e]}
                          onChange={(ev) =>
                            setContent({
                              ...content,
                              candidates: content.candidates.map((x, n) =>
                                n === i
                                  ? {
                                      ...x,
                                      evidence: x.evidence.map((y, m) =>
                                        m === j ? { ...y, [k]: ev.target.value } : y,
                                      ),
                                    }
                                  : x,
                              ),
                            })
                          }
                        />
                      </label>
                    ))}
                    <label>
                      สถานะหลักฐาน
                      <select
                        value={e.uncertainty}
                        onChange={(ev) =>
                          setContent({
                            ...content,
                            candidates: content.candidates.map((x, n) =>
                              n === i
                                ? {
                                    ...x,
                                    evidence: x.evidence.map((y, m) =>
                                      m === j
                                        ? {
                                            ...y,
                                            uncertainty: ev.target.value as typeof e.uncertainty,
                                          }
                                        : y,
                                    ),
                                  }
                                : x,
                            ),
                          })
                        }
                      >
                        {['unverified', 'supported', 'conflicting', 'inaccessible'].map((s) => (
                          <option key={s}>{s}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                ))}
                <label>
                  <input
                    type="radio"
                    name="selected-candidate"
                    checked={content.selectedCandidate === i}
                    onChange={() =>
                      setContent({ ...content, selectedCandidate: i, identityConfirmed: false })
                    }
                  />
                  เลือกตัวตนนี้ · ไม่ลบตัวเลือกอื่น
                </label>
              </section>
            ))}
            {technical ? (
              <>
                <label>
                  <input type="checkbox" checked={content.identityConfirmed} disabled readOnly />
                  ยืนยันตัวตนอ้างอิงสำหรับฉบับนี้
                </label>
                <label>
                  <input type="checkbox" checked={content.briefConfirmed} disabled readOnly />
                  ยืนยันบรีฟสำหรับฉบับนี้
                </label>
                <h2>ต้นทุนประกอบสถานการณ์ · ภายในโรงงาน</h2>
                <p>
                  จำนวนเงินเป็น THB เว้นว่างเมื่อยังไม่ทราบ ใส่ 0
                  เฉพาะรายการที่ยืนยันว่าไม่มีค่าใช้จ่าย พร้อมระบุแหล่งที่มาและวันที่
                  ข้อมูลนี้ไม่ใช่ราคาเสนอสุดท้าย
                </p>
                {(Object.keys(developmentCostLabels) as (keyof typeof developmentCostLabels)[]).map(
                  (label) => {
                    const charge = developmentCharge(content, label);
                    const title = developmentCostLabels[label];
                    return (
                      <fieldset className="card" key={label}>
                        <legend>{title}</legend>
                        <div className="form-grid">
                          <label>
                            จำนวนเงิน THB · {title}
                            <input
                              inputMode="decimal"
                              maxLength={30}
                              value={charge.amountTHB ?? ''}
                              onChange={(e) =>
                                setContent(
                                  updateDevelopmentCharge(content, label, {
                                    amountTHB: chargeAmountFromInput(e.target.value),
                                  }),
                                )
                              }
                            />
                          </label>
                          <label>
                            ฐานค่าใช้จ่าย · {title}
                            <select
                              value={charge.basis}
                              onChange={(e) =>
                                setContent(
                                  updateDevelopmentCharge(content, label, {
                                    basis: e.target.value as typeof charge.basis,
                                  }),
                                )
                              }
                            >
                              <option value="per_unit">ต่อหน่วยบรรจุ</option>
                              <option value="per_batch">ต่อ batch ทั้งหมด</option>
                            </select>
                          </label>
                          <label>
                            ประเภทหลักฐาน · {title}
                            <select
                              value={charge.provenance}
                              onChange={(e) =>
                                setContent(
                                  updateDevelopmentCharge(content, label, {
                                    provenance: e.target.value as typeof charge.provenance,
                                  }),
                                )
                              }
                            >
                              {Object.entries({
                                unknown: 'ยังไม่ทราบ',
                                assumption: 'ข้อสมมติ',
                                historical: 'ข้อมูลย้อนหลัง',
                                quote: 'ใบเสนอราคาต้นทาง',
                              }).map(([value, text]) => (
                                <option value={value} key={value}>
                                  {text}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            แหล่งที่มา · {title}
                            <input
                              value={charge.source}
                              maxLength={4000}
                              onChange={(e) =>
                                setContent(
                                  updateDevelopmentCharge(content, label, {
                                    source: e.target.value,
                                  }),
                                )
                              }
                            />
                          </label>
                          <label>
                            วันที่หลักฐาน · {title}
                            <input
                              type="date"
                              value={charge.date}
                              onChange={(e) =>
                                setContent(
                                  updateDevelopmentCharge(content, label, { date: e.target.value }),
                                )
                              }
                            />
                          </label>
                        </div>
                      </fieldset>
                    );
                  },
                )}
                {content.costs.some(
                  (charge, index) =>
                    !Object.hasOwn(developmentCostLabels, charge.label) ||
                    content.costs.findIndex((c) => c.label === charge.label) !== index,
                ) ? (
                  <aside className="note-danger">
                    <p>
                      รายการค่าใช้จ่ายเดิมนอกห้าหมวดหรือชื่อซ้ำคงอยู่แบบอ่านอย่างเดียว
                      ไม่ตัดออกเมื่อบันทึกฉบับใหม่ โปรดตรวจรายการเดิมใน JSON ภายในก่อนยืนยันต้นทุน
                    </p>
                    <ul>
                      {content.costs
                        .filter(
                          (charge, index) =>
                            !Object.hasOwn(developmentCostLabels, charge.label) ||
                            content.costs.findIndex((c) => c.label === charge.label) !== index,
                        )
                        .map((charge, index) => (
                          <li key={index}>
                            {charge.label} · {charge.amountTHB ?? 'ยังไม่ทราบ'} THB · {charge.basis}{' '}
                            · {charge.provenance}
                          </li>
                        ))}
                    </ul>
                  </aside>
                ) : null}
                <p>
                  บันทึกแล้วเกิดฉบับใหม่ การเลือกของลูกค้าและ R&D/QA ต้องทบทวนฉบับใหม่อีกครั้ง
                  ข้อมูลในฉบับเก่าคงเดิม
                </p>
                <h2>4. แนวทางทดลองสำหรับ R&D</h2>
                <details>
                  <summary>ตรวจสูตรที่เคยผ่านทบทวนในโครงการก่อน</summary>
                  {workspace.records
                    .filter(
                      (r) =>
                        r.kind === 'formula' &&
                        workspace.decisions.some(
                          (d) =>
                            d.recordId === r.id &&
                            d.action === 'review_technical' &&
                            d.disposition === 'passed',
                        ),
                    )
                    .map((r) => (
                      <p key={r.id}>
                        {String(r.content.label)} · {r.id} · v{r.version} · {r.hash.slice(0, 12)}
                      </p>
                    ))}
                  <p>
                    ใช้ส่วนคลังฐานสูตรที่คัดคุณสมบัติด้านล่าง: checker ยืนยันประเภท/สเปก/active แล้ว
                    server เก็บ snapshot ต้นทางก่อนนำเข้า
                  </p>
                </details>
                <button
                  className="action secondary"
                  onClick={() => {
                    try {
                      if (
                        libraryId &&
                        !library.some(
                          (q) =>
                            q.id === libraryId &&
                            q.eligible &&
                            q.category === content.intake.category,
                        )
                      )
                        throw Error(
                          'ฐานที่เลือกไม่เป็นปัจจุบันหรือประเภทไม่ตรง กรุณาโหลดคลังและเลือกใหม่',
                        );
                      setContent({
                        ...content,
                        directions: draftDirections(
                          content,
                          workspace,
                          library.find(
                            (q) =>
                              q.eligible &&
                              q.category === content.intake.category &&
                              (libraryId ? q.id === libraryId : true),
                          )?.direction,
                        ),
                        selectedDirection: null,
                      });
                    } catch (e) {
                      setError(String(e));
                    }
                  }}
                >
                  ค้นคลังที่ผ่านทบทวนก่อน / สร้างไม่เกิน 3 แนวทางทดลอง
                </button>
                {content.directions.map((d, i) => {
                  const cost = indicativeDevelopmentCost(
                    d.lines,
                    content.intake.fillSize,
                    content.intake.fillUnit,
                    content.intake.quantity,
                    content.intake.densityGml,
                    content.intake.yieldFraction,
                    content.costs,
                  );
                  return (
                    <section className="card" key={i}>
                      <h3>{d.label}</h3>
                      <p>
                        {d.origin} · {d.rationale}
                      </p>
                      {selectedDirection === i ? (
                        <p className="note-success">
                          ลูกค้าเลือกแนวทางนี้บนฉบับ v{current?.version} ·{' '}
                          {current?.hash.slice(0, 12)}
                        </p>
                      ) : null}
                      <label>
                        <input
                          type="radio"
                          name="direction"
                          checked={content.selectedDirection === i}
                          onChange={() => setContent({ ...content, selectedDirection: i })}
                        />
                        ข้อเสนอของทีมงานสำหรับร่าง · ยังไม่ใช่การเลือกของลูกค้า
                      </label>
                      {d.lines.map((l, j) => (
                        <div className="form-grid" key={j}>
                          {[
                            'inci',
                            'function',
                            'grade',
                            'percent',
                            'activeFraction',
                            'supplierPricePerKg',
                            'priceSource',
                            'priceDate',
                          ].map((k) => (
                            <label key={k}>
                              {k}
                              <input
                                value={l[k as keyof typeof l] ?? ''}
                                onChange={(e) =>
                                  updateDirection(i, {
                                    ...d,
                                    lines: d.lines.map((x, n) =>
                                      n === j
                                        ? {
                                            ...x,
                                            [k]:
                                              k === 'supplierPricePerKg'
                                                ? e.target.value || null
                                                : e.target.value,
                                          }
                                        : x,
                                    ),
                                  })
                                }
                              />
                            </label>
                          ))}
                          <label>
                            ราคา
                            <select
                              value={l.priceClass}
                              onChange={(e) =>
                                updateDirection(i, {
                                  ...d,
                                  lines: d.lines.map((x, n) =>
                                    n === j
                                      ? { ...x, priceClass: e.target.value as typeof l.priceClass }
                                      : x,
                                  ),
                                })
                              }
                            >
                              {['unknown', 'assumption', 'historical', 'quote'].map((s) => (
                                <option key={s}>{s}</option>
                              ))}
                            </select>
                          </label>
                          <label>
                            เฟส
                            <select
                              value={l.phase}
                              onChange={(e) =>
                                updateDirection(i, {
                                  ...d,
                                  lines: d.lines.map((x, n) =>
                                    n === j ? { ...x, phase: e.target.value as typeof l.phase } : x,
                                  ),
                                })
                              }
                            >
                              {['A', 'B', 'C'].map((s) => (
                                <option key={s}>{s}</option>
                              ))}
                            </select>
                          </label>
                        </div>
                      ))}
                      <div
                        role="status"
                        className={cost.status === 'incomplete' ? 'note-danger' : 'note-success'}
                      >
                        <p>
                          {cost.status === 'incomplete'
                            ? 'ต้นทุนยังไม่ครบ · ยอดรวมเฉพาะรายการที่ทราบ'
                            : 'ต้นทุนประมาณการ · ยังไม่ใช่ราคาเสนอสุดท้าย'}
                        </p>
                        <p>
                          ยอดต่อ batch: {cost.knownSubtotalTHB ?? 'ยังคำนวณไม่ได้'} THB · ต่อหน่วย:{' '}
                          {cost.knownPerUnitTHB ?? 'ยังคำนวณไม่ได้'} THB
                        </p>
                        {cost.missingCharges.length ? (
                          <p>
                            ค่าใช้จ่ายที่ขาดจำนวนเงินหรือหลักฐาน:{' '}
                            {cost.missingCharges
                              .map(
                                (label) =>
                                  developmentCostLabels[
                                    label as keyof typeof developmentCostLabels
                                  ] ?? label,
                              )
                              .join(', ')}
                          </p>
                        ) : null}
                        {cost.missingPrices.length ? (
                          <p>วัตถุดิบที่ยังขาดราคา/หลักฐาน: {cost.missingPrices.join(', ')}</p>
                        ) : null}
                        {cost.issues.length ? (
                          <p>
                            ต้องยืนยันข้อมูลหน่วย จำนวน ความหนาแน่น yield และสูตรก่อนคำนวณ:{' '}
                            {cost.issues.join(', ')}
                          </p>
                        ) : null}
                      </div>
                      <pre className="contract-result">
                        {JSON.stringify(
                          {
                            formula: evaluateDevelopmentFormula(d.lines),
                            cost,
                          },
                          null,
                          2,
                        )}
                      </pre>
                      {Object.entries(technicalLabels).map(([k, label]) => (
                        <label key={k}>
                          {label}
                          <textarea
                            value={
                              typeof d[k as keyof DevelopmentDirection] === 'string'
                                ? (d[k as keyof DevelopmentDirection] as string)
                                : ''
                            }
                            onChange={(e) => updateDirection(i, { ...d, [k]: e.target.value })}
                          />
                        </label>
                      ))}
                    </section>
                  );
                })}
              </>
            ) : null}
            <label>
              เหตุผลฉบับใหม่
              <textarea
                value={content.revisionReason}
                onChange={(e) => setContent({ ...content, revisionReason: e.target.value })}
              />
            </label>
            <button
              className="action"
              onClick={() =>
                void run(async () => {
                  await save();
                })
              }
            >
              บันทึกฉบับที่ตรวจย้อนหลังได้
            </button>
          </fieldset>
          {!technical && content.directions.length ? (
            <section>
              <h2>แนวทางที่แบ่งปัน</h2>
              {content.directions.map((d, i) => (
                <label key={i}>
                  <input
                    type="radio"
                    name="customer-direction"
                    checked={commandDirection === i}
                    disabled={
                      selectedDirection !== null ||
                      selectionRevision?.directionSelection?.state === 'conflict'
                    }
                    onChange={() => setCommandDirection(i)}
                  />
                  {d.label} · {d.origin} · {d.rationale}
                </label>
              ))}
            </section>
          ) : null}
          {selectedDirection !== null && current ? (
            <p role="status" className="note-success">
              ลูกค้าเลือกแนวทาง {selectedDirection + 1} แล้ว · ฉบับ v{current.version} ·{' '}
              {current.hash.slice(0, 12)} การเปลี่ยนต้องใช้ฉบับใหม่
            </p>
          ) : selectionRevision?.directionSelection?.state === 'conflict' ? (
            <p role="alert" className="note-danger">
              การเลือกแนวทางมีข้อขัดแย้ง ประวัติเดิมคงอยู่ ต้องสร้างฉบับใหม่ให้ลูกค้าเลือก
            </p>
          ) : null}
          {context.audience === 'customer' ? (
            <label>
              เลือกตัวตนสำหรับคำสั่งยืนยัน
              <select
                value={commandCandidate ?? ''}
                onChange={(e) =>
                  setCommandCandidate(e.target.value === '' ? null : Number(e.target.value))
                }
              >
                <option value="">ยังไม่เลือก</option>
                {content.candidates.map((c, i) => (
                  <option key={i} value={i}>
                    {c.identity} · {c.variant}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {technical ? (
            <DevelopmentLibrary
              context={context}
              workspace={workspace}
              category={content.intake.category}
              selectedId={libraryId}
              onSelect={setLibraryId}
              onLoad={setLibrary}
            />
          ) : null}
          <h2>5. ตัวอย่าง ความเห็น และทบทวน</h2>
          {current ? (
            <p role="status" style={{ overflowWrap: 'anywhere' }}>
              คำสั่งทบทวน/ยืนยันตรึงฉบับ v{current.version} · hash {current.hash}{' '}
              {dirty ? ' · มีการแก้ไขยังไม่บันทึก กรุณาบันทึกและทบทวนฉบับใหม่ก่อน' : ''}
            </p>
          ) : null}
          {technical && current && context.role === 'rd' ? (
            <section className="scope">
              <h3>ส่งร่างเข้า R&D / คำขอตัวอย่างเดิม</h3>
              <p>
                เติมสเปก/constituents/yield/cleaning ที่มีหลักฐานในทะเบียนเดิมเป็นฉบับใหม่ ให้
                checker ทบทวน แล้วผูกคำขอตัวอย่าง successor ที่ใช้สูตร/กระบวนการ root เดิม
              </p>
              <button
                className="action secondary"
                disabled={busy || !online || dirty || !content.sample.requestId}
                onClick={() =>
                  void run(async () => {
                    await request(`/development/${current.id}/bind-request`, {
                      hash: current.hash,
                      requestId: content.sample.requestId,
                    });
                    setNotice(
                      'ตรึงสูตร กระบวนการ และคำขอตัวอย่าง successor ที่ผ่านทบทวนแล้ว · ต้องบันทึก sample/hash ที่ตรงกันก่อนรับตัวอย่าง',
                    );
                  })
                }
              >
                ตรึงคำขอตัวอย่าง successor ที่ผ่านทบทวน
              </button>
              <label>
                ปริมาณตัวอย่าง g
                <input value={sampleQuantity} onChange={(e) => setSampleQuantity(e.target.value)} />
              </label>
              <label>
                นโยบายตัวอย่างที่ทบทวน
                <select value={samplePolicy} onChange={(e) => setSamplePolicy(e.target.value)}>
                  <option value="">ยังไม่เลือก · ระบบจะเก็บเป็นร่าง</option>
                  {workspace.records
                    .filter((r) => r.kind === 'policy' && r.content.policyType === 'sample')
                    .map((r) => (
                      <option key={r.id} value={r.id}>
                        {String(r.content.label)}
                      </option>
                    ))}
                </select>
              </label>
              <button
                className="action secondary"
                disabled={busy || !online}
                onClick={() =>
                  void run(async () => {
                    const handoff = (await request(`/development/${current.id}/handoff`, {
                      hash: current.hash,
                      quantity: sampleQuantity,
                      policyId: samplePolicy || null,
                      criteria: workspace.records
                        .filter((r) => r.kind === 'criterion')
                        .map((r) => r.id)
                        .slice(0, 20),
                    })) as { formula_id: string; process_id: string; request_id: string };
                    setNotice(
                      `สร้างร่างสูตร ${handoff.formula_id} · กระบวนการ ${handoff.process_id} · คำขอตัวอย่าง ${handoff.request_id} แล้ว · เปิดทะเบียนเพื่อทบทวนและเตรียมตาม gate เดิม`,
                    );
                    setContent({
                      ...content,
                      sample: { ...content.sample, requestId: handoff.request_id },
                    });
                  })
                }
              >
                สร้างร่างสูตร กระบวนการ และคำขอตัวอย่างแบบตรึงฉบับ
              </button>
              <p>
                <a href={`/app/projects/${context.projectId}/formulas`}>เปิดทะเบียนสูตรและทบทวน</a>{' '}
                ·{' '}
                <a href={`/app/projects/${context.projectId}/sample_requests`}>
                  เปิดคำขอตัวอย่างและการเตรียม
                </a>
              </p>
            </section>
          ) : null}
          <p>
            {technical
              ? 'ใช้คำขอ/การเตรียม/การส่งตัวอย่างในระบบเดิม แล้วเลือก ID และ hash ตรงกัน'
              : 'เลือกตัวอย่างที่ได้รับ แล้วบอกสิ่งที่อยากปรับ เช่น ลดความเหนอะหรือลดกลิ่น ความเห็นจะผูกกับตัวอย่างฉบับนี้'}
          </p>
          <label>
            คำขอตัวอย่าง
            <select
              disabled={!author}
              value={content.sample.requestId ?? ''}
              onChange={(e) =>
                setContent({
                  ...content,
                  sample: { ...content.sample, requestId: e.target.value || null },
                })
              }
            >
              <option value="">ยังไม่เลือก</option>
              {workspace.records
                .filter((r) => r.kind === 'sample_request')
                .map((r) => (
                  <option value={r.id} key={r.id}>
                    {String(r.content.label)} v{r.version}
                  </option>
                ))}
            </select>
          </label>
          <label>
            ตัวอย่าง
            <select
              disabled={!author}
              value={content.sample.sampleId ?? ''}
              onChange={(e) => {
                const s = workspace.records.find((r) => r.id === e.target.value);
                setContent({
                  ...content,
                  sample: {
                    ...content.sample,
                    sampleId: s?.id ?? null,
                    sampleHash: s?.hash ?? null,
                  },
                });
              }}
            >
              <option value="">ยังไม่เลือก</option>
              {workspace.records
                .filter((r) => r.kind === 'sample')
                .map((r) => (
                  <option value={r.id} key={r.id}>
                    {String(r.content.label)} v{r.version} · {r.hash.slice(0, 12)}
                  </option>
                ))}
            </select>
          </label>
          <label>
            ความเห็นลูกค้า
            <textarea
              value={commandFeedback}
              onChange={(e) => setCommandFeedback(e.target.value)}
            />
          </label>
          <label>
            คำขอเปลี่ยนแปลง
            <textarea value={commandChange} onChange={(e) => setCommandChange(e.target.value)} />
          </label>
          {current ? (
            <div className="actions">
              {[
                ['share_intake', 'ส่งภาพและรายละเอียดให้ลูกค้ายืนยัน'],
                ['share', 'แบ่งปันข้อเสนอ 3 แนวทางให้ลูกค้า'],
                ['review_rd', 'R&D checker ทบทวน'],
                ['review_compliance', 'QA ทบทวน'],
                ['accept_sample', 'ลูกค้ารับตัวอย่างตรงฉบับ'],
                ['confirm_reference', 'ลูกค้ายืนยันตัวตนอ้างอิง'],
                ['confirm_brief', 'ลูกค้ายืนยันบรีฟ'],
                ['select_direction', 'ลูกค้าเลือกแนวทาง'],
                ['feedback', 'บันทึกความเห็นลูกค้า'],
                ['change_request', 'ส่งคำขอเปลี่ยนแปลง'],
              ]
                .filter(([action]) =>
                  action === 'share' || action === 'share_intake'
                    ? technical
                    : action === 'review_rd'
                      ? context.role === 'rd_checker'
                      : action === 'review_compliance'
                        ? context.role === 'qa'
                        : context.audience === 'customer',
                )
                .map(([action, label]) => (
                  <button
                    key={action}
                    className="action secondary"
                    disabled={
                      busy ||
                      !online ||
                      dirty ||
                      (['share', 'review_rd', 'select_direction'].includes(action ?? '') &&
                        !(technical
                          ? hasThreeCompleteDirections(content)
                          : hasThreeComparedSummaries(content)))
                    }
                    onClick={() =>
                      void run(async () => {
                        if (dirty)
                          throw Error('มีการแก้ไขที่ยังไม่บันทึก กรุณาบันทึกและทบทวนฉบับใหม่ก่อน');
                        await request(`/development/${current.id}/decision`, {
                          hash: current.hash,
                          action,
                          selection:
                            action === 'confirm_reference'
                              ? commandCandidate
                              : action === 'select_direction'
                                ? commandDirection
                                : null,
                          comment:
                            (action === 'change_request' ? commandChange : commandFeedback) ||
                            'ทบทวนฉบับที่บันทึกนี้ตามขอบเขต · ยังไม่อนุญาตผลิตจริง',
                        });
                        const fresh = await refresh();
                        if (!fresh) return;
                        const row = fresh.revisions.find((r) => r.id === current.id);
                        if (row) select(row);
                      })
                    }
                  >
                    {label}
                  </button>
                ))}
            </div>
          ) : null}
          <h3>การตัดสินใจและความเห็นของฉบับนี้</h3>
          {data.decisions
            .filter((d) => d.revisionId === current?.id)
            .map((d, i) => (
              <p key={i}>
                {d.action}
                {d.selection !== null && d.selection !== undefined
                  ? ` · ตัวเลือก ${d.selection + 1}`
                  : ''}{' '}
                · {d.comment}
              </p>
            ))}
          <h2>เรื่องที่ยังรอ</h2>
          <ul>
            {developmentBlockers(
              content,
              selectedDecisions,
              selectionRevision ?? undefined,
              technical,
            ).map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
          <section className="scope">
            <h3>AI / OCR</h3>
            {aiRun && current ? (
              <div className="actions">
                {['execute', 'cancel', 'status'].map((action) => (
                  <button
                    key={action}
                    className="action secondary"
                    disabled={!online}
                    onClick={() =>
                      void request(`/development/${current.id}/ai/${aiRun.id}/${action}`, {})
                        .then((r) => {
                          setAiRun(r as { id: string; state: string });
                          setNotice('SOFTWARE TEST ONLY: ' + (r as { state: string }).state);
                        })
                        .catch(() => setError('AI run unavailable'))
                    }
                  >
                    {action === 'execute'
                      ? 'เรียก test adapter'
                      : action === 'cancel'
                        ? 'ยกเลิก AI run'
                        : 'ตรวจสถานะ / interrupted recovery'}
                  </button>
                ))}
              </div>
            ) : null}
            <p>
              ยังไม่มี provider OCR/vision/search/formulation สด · กรอกข้อความและหลักฐานเองได้ ·
              ข้อความบนฉลากเป็นข้อมูล ไม่ใช่คำสั่งระบบ
            </p>
            {current ? (
              <>
                <button
                  disabled={busy || !online}
                  className="action secondary"
                  onClick={() =>
                    void run(async () => {
                      const r = (await request(`/development/${current.id}/ai`, {
                        adapter: 'manual',
                      })) as { state: string };
                      setNotice('AI: ' + r.state + ' · ใช้ OCR/evidence ที่กรอกเอง');
                    })
                  }
                >
                  บันทึกสถานะ manual fallback
                </button>
                <button
                  disabled={busy || !online}
                  className="action secondary"
                  onClick={() =>
                    void run(async () => {
                      const r = (await request(`/development/${current.id}/ai`, {
                        adapter: 'software_test',
                      })) as { id: string; state: string };
                      setAiRun(r);
                      setNotice('SOFTWARE TEST ONLY: ' + r.state);
                    })
                  }
                >
                  ทดสอบ adapter · ไม่ใช่งานวิจัยสด
                </button>
              </>
            ) : null}
          </section>
          {current ? (
            <div className="actions">
              {(
                ['customer', ...(technical ? ['internal'] : [])] as ('customer' | 'internal')[]
              ).flatMap((a) =>
                [false, true].map((pdf) => (
                  <button
                    key={a + String(pdf)}
                    disabled={busy || !online}
                    className="action secondary"
                    onClick={() => void run(() => exportSnapshot(a, pdf))}
                  >
                    {a === 'internal' ? 'ใบงาน R&D ภายใน' : 'บรีฟลูกค้า'} {pdf ? 'PDF' : 'JSON'} ·
                    ฉบับที่บันทึก
                  </button>
                )),
              )}
            </div>
          ) : null}
          {notice ? <p role="status">{notice}</p> : null}
          {error ? (
            <p className="note-danger" role="alert">
              {error}
            </p>
          ) : null}
          <p className="scope">DEMO — NOT FOR REGULATORY USE — ไม่ใช่ข้อมูลสำหรับใช้ยื่นจริง</p>
        </section>
      </div>
    </>
  );
}
